import React, { useState, useEffect, useCallback } from "react";
import { Input, Table, Space, Tooltip } from "antd";
import "./transferIds.css";
import TransferIdActionDialog from "../TransferIdActionDialog/transfer-id-action-dialog";
import { fetchTransferIdDetails } from "../../../../services/fetch-transfer-id-details";
import { updateTransferIdDetails } from "../../../../services/update-transfer-id-details";
import { getUserName } from "../../../../services/utils";
import {
  wrappedText,
  formatToLongDate,
} from "../../../../components/Shared/utils";
import SubHeading from "../../../../components/SubHeading/sub-heading";
import { useOpenNotification } from "../../../../components/Notification/notification";
import { formatDateToISO } from "./utils";
import {
  filterByDate,
  sortByDate,
  sortByUpdatedBy,
} from "../../../PackageHistory/table-sort-filter";
import { useNavigate, Outlet } from "react-router-dom";

interface TransferIdsProps {
  activeKey?: string;
}

const TransferIds: React.FC<TransferIdsProps> = ({ activeKey }) => {
  const { Search } = Input;
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [id, setId] = useState<number>(0);
  const [status, setStatus] = useState<string>("Enabled");
  const [tableData, setTableData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>("");
  const { openNotification, contextHolder } = useOpenNotification();
  const userName = getUserName();
  type SortOrder = "ascend" | "descend" | null;

  const getUniqueValues = (field: string) => {
    const uniqueValues: Set<string> = new Set();
    tableData.forEach((item: any) => {
      const value = item[field];
      if (value) {
        const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;
        if (isoDateRegex.test(value)) {
          const formattedDate = formatToLongDate(value);
          uniqueValues.add(formattedDate);
        } else {
          uniqueValues.add(value);
        }
      }
    });

    const temp = Array.from(uniqueValues)
      .map((value) => String(value))
      .sort((a: string, b: string) => {
      // Try to parse as numbers
      const numA = Number(a);
      const numB = Number(b);

      // If both are valid numbers, compare numerically
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }

      // Try to parse as dates
      const dateA = new Date(a);
      const dateB = new Date(b);
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        return dateA.getTime() - dateB.getTime();
      }

      // Fall back to string comparison
      return a.localeCompare(b);
      })
      .map((value) => ({ text: value, value }));

    return temp;
  };

  const fetchData = useCallback(() => {
    fetchTransferIdDetails().then((result) => {
      if (result) {
        setTableData(result.data);
        setFilteredData(result.data);
        setLoading(false);
      } else {
        setLoading(false);
        openNotification("error", "failed to load transferId List");
      }
      setSearchValue("");
    });
  }, [openNotification]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      title: "Number",
      dataIndex: "number",
      editable: true,
      width: "15%",
      key: "number",
      defaultSortOrder: "ascend" as SortOrder,
      filters: getUniqueValues("number"),
      onFilter: (value: any, record: any) => record.number === value,
      sorter: (a: any, b: any) => a.number - b.number,
    },
    {
      title: "Name",
      dataIndex: "name",
      editable: true,
      width: "35%",
      key: "name",
      filters: getUniqueValues("name"),
      render: (text: any) => (
        <Tooltip title={text} placement="right">
          {" "}
          {wrappedText(text, 60)}{" "}
        </Tooltip>
      ),
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
      onFilter: (value: any, record: any) => record.name === value,
    },
    {
      title: "Description",
      dataIndex: "description",
      editable: true,
      width: "35%",
      key: "description",
      filters: getUniqueValues("description"),
      render: (text: any) => (
        text ? (
          <Tooltip
            title={<span style={{ whiteSpace: "pre-wrap" }}>{text}</span>}
            placement="right"
          >
            <span style={{ whiteSpace: "pre-wrap" }}>
              {text.length > 50 ? text.replace(/\n/g, "\n").slice(0, 50) + "..." : text}
            </span>
          </Tooltip>
        ) : null
      ),
      sorter: (a: any, b: any) => a.description.localeCompare(b.description),
      onFilter: (value: any, record: any) => record.description === value,
    },
    {
      title: "Last Updated Date",
      dataIndex: "last_modified_on",
      editable: true,
      width: "15%",
      key: "last_modified_on",
      render: (text: any) =>
        text !== null && text !== "" ? formatToLongDate(text) : "-",
      filters: getUniqueValues("last_modified_on"),
      sorter: (a: any, b: any) => sortByDate(a, b, "last_modified_on"),
      onFilter: (value: any, record: any) =>
        filterByDate(value, record.last_modified_on),
    },
    {
      title: "Last Updated By",
      dataIndex: "last_modified_by",
      editable: true,
      width: "20%",
      key: "last_modified_by",
      render: (text: any) => (text !== null && text !== "" ? text : "-"),
      filters: getUniqueValues("last_modified_by"),
      sorter: (a: any, b: any) => sortByUpdatedBy(a, b, "last_modified_by"),
      onFilter: (value: any, record: any) => record.last_modified_by === value,
    },
    {
      title: "Action",
      dataIndex: "Action",
      editable: true,
      width: "15%",
      render: (text: any, record: any) => {
        return (
          <Space>
            <button
              onClick={() => handlePageChange(false, record.number)}
              disabled={record.status === "disabled"}
              style={{
              background: "none",
              border: "none",
              color: record.status === "disabled" ? "grey" : "#008576",
              cursor: record.status === "disabled" ? "not-allowed" : "pointer",
              padding: 0,
              }}
            >
              Edit
            </button>
            <button
              onClick={() => handleClick(record)}
              style={{
              background: "none",
              border: "none",
              color: "#008576",
              cursor: "pointer",
              padding: 0,
              }}
            >
              {record.status === "disabled" ? "Enable" : "Disable"}
            </button>
          </Space>
        );
      },
    },
  ];

  const isValidDate = (dateStr: string): boolean => {
    if (dateStr.includes("-")) {
      const date = new Date(dateStr);
      return !isNaN(date.getTime());
    }
    return false;
  };

  const handleSearch = (value: string) => {
    // normalize: remove \r and \n, collapse multiple spaces, lowercase and trim
    const normalize = (s: any) =>
      String(s ?? "")
        .replace(/\r?\n/g, " ")// remove newlines
        .replace(/\s+/g, " ")// collapse multiple whitespace to single space
        .toLowerCase()
        .trim();

    const searchableValue = isValidDate(value)
      ? formatDateToISO(value).toLowerCase()
      : value.toLowerCase();

    const filtered = tableData.filter((item: any) =>
      Object.values(item).some((field) =>
        normalize(field).includes(searchableValue.trim()),
      ),
    );

    setFilteredData(filtered);
  };

  const handleToggle = () => {
    updateTransferIdDetails({
      number: id,
      status: status === "disabled" ? "enable" : "disable",
      modifiedBy: userName,
    }).then((response) => {
      if (response) {
        if (status === "disabled") {
          openNotification("success", "Transfer ID has been enabled");
        } else {
          openNotification("success", "Transfer ID has been disabled");
        }
        fetchData();
      } else {
        openNotification("error", "failed to update status");
      }
    });
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClick = (record: any) => {
    setOpen(true);
    setId(record.number);
    setStatus(record.status);
  };

  const handlePageChange = (flow: boolean, rowId?: number) => {
    navigate(`/admin/transfer-ids/edit/${rowId}`);
  };

  return (
    <>
      <div className="dashboard-wrapper" id="dashboard-wrapper-title">
        <SubHeading
          title="Transfer IDs"
          buttonName="Create New"
          onClickNavigate={() => { navigate("/admin/transfer-ids/create"); }}
        />
        <div style={{ backgroundColor: "white" }}>
          <div className="search">
            <Search
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
              allowClear={true}
            />
          </div>
          <div style={{ margin: "0 15px" }}>
            <Table
              columns={columns}
              dataSource={filteredData}
              size="small"
              tableLayout="fixed"
              rowClassName={(record) =>
                record.status === "disabled" ? "dark-grey-row" : ""
              }
              className="filter"
              pagination={{ pageSize: 6, showSizeChanger: false }}
              loading={loading}
            />
          </div>
        </div>
        <TransferIdActionDialog
          status={status}
          open={open}
          handleCancel={handleClose}
          handleSubmit={handleToggle}
        />
      </div>
      {contextHolder}
      <Outlet />
    </>
  );
};

export default TransferIds;
